import type { ReactNode } from 'react';

import { useTranslation } from 'react-i18next';
import { useCurrentWorkspaceLogoUrl, useCurrentWorkspaceName, useMediaUrl } from '@/shared/hooks';

import { InstagramPreview } from './previews/InstagramPreview';
import { LinkedInPreview } from './previews/LinkedInPreview';
import { TikTokPreview } from './previews/TikTokPreview';
import { WebsitePreview } from './previews/WebsitePreview';
import { XPreview } from './previews/XPreview';

// Accepts any platform string. The Content Test dropdown writes the supported product
// platforms; unknown values fall back to WebsitePreview.
interface ContentPhonePreviewProps {
  caption: string;
  mediaAssetId?: string;
  mediaType?: 'image' | 'video';
  // When provided, replaces the default media block inside the phone frame.
  // Variant cards use this to inject their own loading/retry-aware media.
  mediaSlot?: ReactNode;
  platform: string | undefined;
}

// eslint-disable-next-line i18next/no-literal-string -- placeholder account handle, slugified like a real one
const FALLBACK_HANDLE = 'yourbrand';

const toHandle = (name: string): string => name.toLowerCase().replace(/[^a-z0-9]/g, '');

export const ContentPhonePreview = ({
  caption,
  mediaAssetId,
  mediaType,
  mediaSlot,
  platform,
}: ContentPhonePreviewProps) => {
  const { t } = useTranslation('contentTest');
  const { downloadUrl } = useMediaUrl(mediaAssetId, mediaType);
  const workspaceName = useCurrentWorkspaceName();
  const avatarUrl = useCurrentWorkspaceLogoUrl();

  const displayName = workspaceName ?? t('contentTest.contentPhonePreview.yourBrand');
  const handle = workspaceName ? toHandle(workspaceName) || FALLBACK_HANDLE : FALLBACK_HANDLE;

  const props = {
    caption,
    mediaUrl: downloadUrl,
    mediaType,
    mediaSlot,
    displayName,
    handle,
    avatarUrl,
  };

  switch (platform) {
    case 'TikTok':
      return <TikTokPreview {...props} />;
    case 'Instagram':
      return <InstagramPreview {...props} />;
    case 'LinkedIn':
      return <LinkedInPreview {...props} />;
    case 'Twitter/X':
      return <XPreview {...props} />;
    case 'Website':
      return <WebsitePreview {...props} />;
    default:
      return <WebsitePreview {...props} />;
  }
};

ContentPhonePreview.displayName = 'ContentPhonePreview';
